import { useContext, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import Banner from './Banner';
import SkillApi from '../../api/skillApi';
import UserProfileApi from '../../api/userProfileApi';
import IconButtonLink from '../../components/atoms/anchor/IconButtonLink';
import UserRoundedImage from '../../components/atoms/rounded-image/UserRoundedImage';
import SocialLinkButtons from '../../components/molecules/socialLinkButtons/SocialLinkButtons';
import Card from '../../components/organisms/Card';
import DiscussionSwitchSidebarContent from '../../components/organisms/sidebar/discussion/DiscussionSwitchSidebarContent';
import { DoubleTabSidebar } from '../../components/organisms/sidebar/Sidebar';
import { SkillProgressSidebarContent } from '../../components/organisms/sidebar/SkillProgressSidebarContent';
import HeaderArea from '../../components/page-layout/HeaderArea';
import MainArea from '../../components/page-layout/MainArea';
import PageRightSidebar from '../../components/page-layout/PageRightSidebar';
import SidebarArea from '../../components/page-layout/SidebarArea';
import { Breadcrumbs } from '../../types/breadcrumbs';
import SkillData from '../../types/skillData';
import UserProfileData from '../../types/userProfileData';
import { AuthContext } from '../../utils/AuthProvider';
import { textToParagraphs } from '../../utils/functions';

import './SkillDetail.scss';
import { useIsModerator } from '../../utils/hooks';

export default function SkillDetail() {
  const [skill, setSkill] = useState<SkillData>();

  const { user } = useContext(AuthContext)!;
  const [authorProfile, setAuthorProfile] = useState<UserProfileData | null>();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumbs>([
    ['/home', 'Home'],
    ['#', 'Loading...'],
  ]);
  const isAuthor = skill?.authorUuid !== undefined && skill.authorUuid === user?.uid;
  const isModerator = useIsModerator(user);

  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    async function fetchData() {
      const fetchedSkill = await SkillApi.getSkillBySlug(slug!);
      const fetchedAuthor = await UserProfileApi.getUserProfileByUuid(fetchedSkill.authorUuid);
      setSkill(fetchedSkill);
      setAuthorProfile(fetchedAuthor);
      setBreadcrumbs([
        ['/home', 'Home'],
        ['#', fetchedSkill.name],
      ]);
    }

    fetchData();
  }, [slug, user]);

  return (
    <PageRightSidebar className="skill-detail" breadcrumbs={breadcrumbs}>
      <HeaderArea>
        <div className="flex">
          <h1 className="strong-heading">{skill?.name}</h1>
          {(isAuthor || isModerator) && skill && (
            <>
              <IconButtonLink className="ml-4" icon="edit-outline" to={`/skills/${skill.slug}/edit`} paddingSize="sm" />
              <IconButtonLink
                className="ml-4"
                icon="monitoring"
                to={`/created-skills/statistics/${skill!.slug}`}
                paddingSize="sm"
              />
            </>
          )}
        </div>
        <p>{authorProfile && authorProfile.firstName + ' ' + authorProfile.lastName}</p>
      </HeaderArea>
      <MainArea>
        {skill && <Banner className="mb-16" imageUrl={skill.imageUrl} user={user!} skill={skill} />}
        <div className="cards-container">
          <div className="description-container mr-3">
            <h2 className="strong-heading-mb">Description</h2>
            <Card className="card-fixed-height description p-6">{textToParagraphs(skill?.description)}</Card>
          </div>

          <div className="author-container">
            <h2 className="strong-heading-mb">About the author</h2>
            <Card className="card-fixed-height p-6">
              <div className="author-card">
                <p className="author-description">
                  {authorProfile?.title ? authorProfile.description : "Author's description"}
                </p>

                <header className="author-header">
                  <div>
                    {authorProfile && <UserRoundedImage className="shrink-0" size="md" userProfile={authorProfile} />}
                    <p className="text-lg font-medium mb-1">
                      {authorProfile ? `${authorProfile.firstName} ${authorProfile.lastName}` : "Author's name"}
                    </p>
                    <p className="text-xs text-gray-500">{authorProfile?.title || 'Title'}</p>
                  </div>
                  <div className="social-media-links">
                    {authorProfile && <SocialLinkButtons userProfile={authorProfile}></SocialLinkButtons>}
                  </div>
                </header>
              </div>
            </Card>
          </div>
        </div>
      </MainArea>

      <SidebarArea>
        <DoubleTabSidebar
          tabLabels={['Your progress', 'Discussion']}
          component1={skill && <SkillProgressSidebarContent showSectionManagerButton skill={skill} />}
          component2={skill && <DiscussionSwitchSidebarContent contextId={skill.id} context="SKILL" />}
        />
      </SidebarArea>
    </PageRightSidebar>
  );
}
